import { useState, useCallback } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SurveyFilters } from '../ui'
import { LoadingState, ConfirmModal } from '../common';
import SurveyList from './SurveyList';
import { toast } from 'react-toastify';
import { deleteSurvey, archiveSurvey } from '../../api/survey.api';
import storage from '../../utils/storage';
import UserHeader from '../users/UserHeader';
import { useSurveys } from '../../hooks/useSurveys';

const STATUSES = [
    { key: "all", label: "Tous" },
    { key: "active", label: "Actifs" },
    { key: "closed", label: "Clôturés" },
];

const GerantSurveys = () => {
    const navigate = useNavigate()
    const { pathname } = useLocation();
    const { surveys, isLoading, computeProgress, getStatusConfig } = useSurveys();
    const [filter, setFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, survey: null });
    const [actionLoading, setActionLoading] = useState(false);
    const role = storage.getRole()

    // Vérifier si on est sur une route enfant (nouveau, :id, modifier/:id)
    const isChildRoute = pathname !== '/gerant/gestion-sondages';

    // Tous les hooks AVANT les returns conditionnels
    const handleView = useCallback((survey) => {
        navigate(role === 'ADMIN' ?  `/admin/gestion-sondage/${survey.id}` : `/gerant/gestion-sondages/${survey.id}`)
    }, [navigate, role]);

    const handleEdit = useCallback((survey) => {
        navigate(`modifier/${survey.id}`);
    }, [navigate]);

    const handleDeleteClick = useCallback((survey) => {
        setConfirmModal({
            isOpen: true,
            type: 'delete',
            survey,
        });
    }, []);

    const handleArchiveClick = useCallback((survey) => {
        setConfirmModal({
            isOpen: true,
            type: 'archive',
            survey,
        });
    }, []);

    const handleConfirmAction = useCallback(async () => {
        const { type, survey } = confirmModal;
        setActionLoading(true);

        try {
            if (type === 'archive') {
                await archiveSurvey(survey.id, !survey.archived);
                toast.success(survey.archived ? 'Sondage désarchivé avec succès !' : 'Sondage archivé avec succès !');
            } else if (type === 'delete') {
                await deleteSurvey(survey.id);
                toast.success('Sondage supprimé avec succès !');
            }
            setConfirmModal({ isOpen: false, type: null, survey: null });
        } catch (err) {
            console.error('Erreur lors de l\'action:', err);
            const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Erreur lors de l\'action';
            toast.error(errorMsg);
        } finally {
            setActionLoading(false);
        }
    }, [confirmModal]);

    // Maintenant les returns conditionnels
    if (isChildRoute) {
        return <Outlet />;
    }

    if (isLoading && surveys.length === 0) {
        return <LoadingState message="Chargement des sondages..." />;
    }

    // Filtrer les sondages
    const filtered = surveys.filter(s => {
        const matchesFilter = filter === "all" || 
          (filter === "active" && s.computed_status === "Actif") ||
          (filter === "closed" && s.computed_status === "Clôturé");
        
        const matchesSearch = !searchTerm || 
          s.title?.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesFilter && matchesSearch;
    });

    const userRole = storage.getRole();
    const showActionButtons = userRole !== 'ADMIN';

    return (
        <>
        <div className="space-y-6 scrollbar-hide">
            <UserHeader
                title="Mes sondages"
                subtitle="sondages enregistrés"
                totalUsers={surveys.length}
                onCreate={() => navigate('nouveau')}
                createLabel="Nouveau sondage"
            />
            <SurveyFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filter={filter}
                setFilter={setFilter}
                statuses={STATUSES}
            />
             <SurveyList
                surveys={filtered}
                onView={handleView}
                onEdit={showActionButtons ? handleEdit : null}
                onArchive={showActionButtons ? handleArchiveClick : null}
                onDelete={showActionButtons ? handleDeleteClick : null}
                isLoading={isLoading || actionLoading}
                computeProgress={computeProgress}
                getStatusConfig={getStatusConfig}
            />
        </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, type: null, survey: null })}
                onConfirm={handleConfirmAction}
                title={confirmModal.type === 'archive' ? 'Archiver le sondage' : 'Supprimer le sondage'}
                description={
                    confirmModal.type === 'archive'
                        ? `Êtes-vous sûr de vouloir ${confirmModal.survey?.archived ? 'désarchiver' : 'archiver'} "${confirmModal.survey?.title}" ?`
                        : `Êtes-vous sûr de vouloir supprimer "${confirmModal.survey?.title}" ? Cette action est irréversible.`
                }
                confirmText={confirmModal.type === 'archive' ? (confirmModal.survey?.archived ? 'Désarchiver' : 'Archiver') : 'Supprimer'}
                variant={confirmModal.type === 'delete' ? 'danger' : 'warning'}
                loading={actionLoading}
            />
        </>
    )
}

export default GerantSurveys